/* eslint-disable no-param-reassign */
import { paths } from '@constants';
import * as Sentry from '@sentry/react';
import { logError, logInfo } from '@utils-global';
import { AxiosError, HttpStatusCode } from 'axios';

/**
 * Extracts claims challenge from WWW-Authenticate header
 * Format: Bearer realm="", authorization_uri="", client_id="", error="insufficient_claims", claims="base64_encoded_claims"
 */
const extractClaimsFromWWWAuthenticate = (header: string | undefined): string | null => {
  if (!header) return null;

  try {
    // Extract claims parameter from WWW-Authenticate header
    const claimsMatch = header.match(/claims="([^"]+)"/);
    if (!claimsMatch || !claimsMatch[1]) return null;

    // Decode base64 claims
    const decodedClaims = atob(claimsMatch[1]);
    logInfo('Extracted CAE claims challenge:', decodedClaims);
    return decodedClaims;
  } catch (error) {
    logError('Failed to extract claims from WWW-Authenticate header', error);
    return null;
  }
};

export const graphErrorInterceptor = (error: AxiosError) => {
  const errorData = error.response?.data as { error?: { code?: string; message?: string } };
  const wwwAuthHeader = error.response?.headers?.['www-authenticate'];

  // Add detailed breadcrumb for Sentry debugging
  Sentry.addBreadcrumb({
    category: 'graph-api',
    level: 'error',
    message: `Graph API error: ${error.message}`,
    data: {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      axiosCode: error.code,
      errorCode: errorData?.error?.code,
      errorMessage: errorData?.error?.message,
      requestId: error.response?.headers?.['request-id'],
      clientRequestId: error.response?.headers?.['client-request-id'],
      wwwAuthenticate: wwwAuthHeader,
      // Not JSON.stringify(error): its config carries the Authorization header.
    },
  });

  if (error.response) {
    if (error.response.status === HttpStatusCode.Unauthorized) {
      // Check if this is a CAE challenge (Continuous Access Evaluation)
      const claims = extractClaimsFromWWWAuthenticate(wwwAuthHeader);

      if (claims) {
        logInfo('CAE challenge detected - claims required for token refresh:', claims);

        // Attach claims to error so it can be handled by the caller
        // The caller should use MSAL to acquire a new token with these claims
        (error as { claims?: string; isCaeChallenge?: boolean }).claims = claims;
        (error as { claims?: string; isCaeChallenge?: boolean }).isCaeChallenge = true;

        // Don't redirect to logout for CAE challenges - they can be recovered
        return Promise.reject(error);
      }

      // if the AT has expired without CAE challenge - push to logout page
      window.location.hash = paths.logout;
    }
  }

  return Promise.reject(error);
};
